/** Tonight's stats — the scoring rundown of the game this promotion follows.
 *
 *  Same shape as the Live scores panel: our own server, polled while the panel
 *  is open and the page is visible, and it closes itself the moment a round
 *  opens, because the scratch card is why the fan is holding the phone.
 *
 *  Each league shows what its own feed really publishes:
 *    • NHL — every goal with its time, the scorer and the assists.
 *    • NFL and NBA — every scoring play with its clock and the league's own
 *      wording, which names the scorer (and any assist).
 *    • any other league — a plain "not available for this league yet".
 */
import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { GoalTapelligenceFooter } from './GoalTapelligenceFooter';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game`;
const EVERY = 20000;

const periodLabel = (row, t) => {
    if (row.kind === 'ot') return t('goalGame.statsOvertime');
    if (row.kind === 'so') return t('goalGame.statsShootout');
    if (row.kind === 'quarter') return t('goalGame.statsQuarter', { n: row.number });
    return t('goalGame.statsPeriod', { n: row.number });
};

const Play = ({ play, t }) => (
    <div className="flex gap-2 py-1.5 border-b border-white/10 last:border-0"
         data-testid="gs-play">
        <span className="text-xs tabular-nums opacity-60 w-12 shrink-0">{play.time}</span>
        <span className="text-xs font-bold w-10 shrink-0">{play.team}</span>
        <div className="min-w-0 grow">
            {play.scorer ? (
                <p className="text-sm font-semibold">{play.scorer}</p>
            ) : (
                <p className="text-sm">{play.text}</p>
            )}
            {play.assists?.length > 0 && (
                <p className="text-xs opacity-60">
                    {t('goalGame.statsAssists')}: {play.assists.join(', ')}
                </p>
            )}
        </div>
        <span className="text-xs tabular-nums opacity-70 shrink-0">{play.score}</span>
    </div>
);

export const GoalStatsPanel = ({ cardId, t, title, brand, c, ty, logoUrl, onClose, framed = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!cardId) { setData({ available: false, reason: 'no_game' }); setLoading(false); return; }
        try {
            const res = await fetch(`${API}/public/${cardId}/game-stats`);
            const body = res.ok ? await res.json() : null;
            setData(body || { available: false, reason: 'feed_unavailable' });
        } catch (_e) {
            setData((prev) => prev || { available: false, reason: 'feed_unavailable' });
        } finally { setLoading(false); }
    }, [cardId]);

    useEffect(() => {
        load();
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, EVERY);
        return () => clearInterval(id);
    }, [load]);

    const live = data?.state === 'live';
    const head = data?.available
        ? [data.label,
           data.away ? `${data.away.abbrev} ${data.away.score ?? 0} – ${data.home.abbrev} ${data.home.score ?? 0}` : '',
           live
               ? (data.intermission
                   ? t('goalGame.scoreIntermission')
                   : (data.status_detail
                       || `${periodLabel({ kind: data.league === 'nhl' ? 'period' : 'quarter', number: data.period }, t)}${data.clock ? `, ${data.clock}` : ''}`))
               : (data.state === 'final' ? t('goalGame.scoreFinal') : '')].filter(Boolean)
        : [];

    return (
        <div className={`${framed ? 'absolute' : 'fixed'} inset-0 z-40 flex items-end`} data-testid="gs-panel-wrap">
            <button type="button" aria-label="close" onClick={onClose} data-testid="gs-scrim"
                    className="absolute inset-0 bg-black/60" />
            <div className="relative w-full max-h-[88%] overflow-y-auto rounded-t-3xl border-t border-white/15 px-4 pt-4 pb-8 sw-rise"
                 style={{ backgroundColor: c.bg || '#0b1220', color: c.text, ...ty.body }}
                 data-testid="gs-panel">
                <div className="flex items-center justify-between gap-3 mb-4">
                    {logoUrl ? (
                        <img src={logoUrl} alt={brand?.organization_name || ''} data-testid="gs-panel-logo"
                             style={{ height: `${Math.min(brand?.logo_size_px || 40, 48)}px` }}
                             className="object-contain" />
                    ) : (
                        <p className="text-sm font-semibold opacity-80" data-testid="gs-panel-logo">
                            {brand?.organization_name || ''}
                        </p>
                    )}
                    <button type="button" onClick={onClose} data-testid="gs-close"
                            className="rounded-full border border-white/25 p-1.5">
                        <X size={16} />
                    </button>
                </div>

                <p className="text-lg font-bold mb-1" style={ty.headline} data-testid="gs-panel-title">
                    {title || t('goalGame.statsTitle')}
                </p>

                {loading && !data ? (
                    <p className="py-6 text-center text-sm opacity-60" data-testid="gs-loading">…</p>
                ) : !data?.available ? (
                    <p className="py-6 text-center text-sm opacity-70" data-testid="gs-unavailable">
                        {data?.reason === 'league_not_supported'
                            ? t('goalGame.statsLeagueUnavailable')
                            : t('goalGame.statsNoGame')}
                    </p>
                ) : (
                    <div data-testid="gs-body">
                        <p className="text-sm font-semibold mb-3 tabular-nums" data-testid="gs-head">
                            {head.join(' · ')}
                        </p>
                        {(data.periods || []).map((row) => (
                            <div key={`${row.kind}-${row.number}`} className="mb-4"
                                 data-testid={`gs-period-${row.number}`}>
                                <p className="text-xs uppercase tracking-wider opacity-60 mb-1">
                                    {periodLabel(row, t)}
                                    {row.current ? ` — ${t('goalGame.statsSoFar')}` : ''}
                                </p>
                                {row.plays.length === 0 ? (
                                    <p className="text-xs opacity-50 py-1" data-testid={`gs-period-empty-${row.number}`}>
                                        {t('goalGame.statsNoScoring')}
                                    </p>
                                ) : row.plays.map((play, i) => (
                                    <Play key={i} play={play} t={t} />
                                ))}
                            </div>
                        ))}
                        {data.age_seconds != null && (
                            <p className="mt-2 text-[11px] opacity-50" data-testid="gs-updated">
                                {t('goalGame.scoreUpdated', { seconds: data.age_seconds })}
                            </p>
                        )}
                    </div>
                )}
                <GoalTapelligenceFooter />
            </div>
        </div>
    );
};

export default GoalStatsPanel;
